import { useQuery } from '@tanstack/react-query';
import type { IImportOptionRo, IInplaceImportOptionRo } from '@teable/openapi';
import { getFields as apiGetFields, getTableById as apiGetTableById } from '@teable/openapi';
import { ReactQueryKeys } from '@/sdk/config';
import { useHookPermission } from '@/sdk/hooks';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { InplaceImportOptionPanel } from '../CollapsePanel';
import { InplacePreviewColumn } from './InplacePreviewColumn';

interface IInplaceFieldConfigPanel {
  tableId: string;
  workSheets: IImportOptionRo['worksheets'];
  errorMessage: string;
  insertConfig: IInplaceImportOptionRo['insertConfig'];
  onChange: (value: IInplaceImportOptionRo['insertConfig']) => void;
}

export type IInplaceOption = Pick<
  IInplaceImportOptionRo['insertConfig'],
  'excludeFirstRow' | 'sourceWorkSheetKey'
>;

const InplaceFieldConfigPanel = (props: IInplaceFieldConfigPanel) => {
  const { t } = useTranslation(['table']);
  const { tableId, workSheets, insertConfig, onChange, errorMessage } = props;

  const permission = useHookPermission();

  const options: IInplaceOption = useMemo(
    () => ({
      excludeFirstRow: insertConfig.excludeFirstRow,
      sourceWorkSheetKey: insertConfig.sourceWorkSheetKey,
    }),
    [insertConfig]
  );

  const { data: table } = useQuery({
    queryKey: ReactQueryKeys.tableInfo(tableId),
    queryFn: () => apiGetTableById(tableId).then((data) => data.data),
  });

  const { data: fields } = useQuery({
    queryKey: ReactQueryKeys.field(tableId),
    queryFn: () => apiGetFields(tableId).then((data) => data.data),
  });

  // Check if user has table manage permission - if so, they can read all fields
  const hasTableManage = permission['table|manage'] ?? false;
  const fieldWithPermission = hasTableManage ? fields : [];

  const optionHandler = (value: IInplaceOption, propertyName: keyof IInplaceOption) => {
    const newInsertConfig = {
      ...insertConfig,
      ...value,
    };
    if (propertyName === 'sourceWorkSheetKey') {
      newInsertConfig.sourceColumnMap = {};
    }
    onChange(newInsertConfig);
  };

  const columnHandler = (value: IInplaceImportOptionRo['insertConfig']['sourceColumnMap']) => {
    if (
      !isEqual(insertConfig.sourceColumnMap, {
        ...insertConfig.sourceColumnMap,
        ...value,
      })
    ) {
      onChange({
        ...insertConfig,
        ['sourceColumnMap']: {
          ...insertConfig.sourceColumnMap,
          ...value,
        },
      });
    }
  };

  return (
    <div className="flex flex-col">
      <div>
        <p className="text-base font-bold">
          {t('table:import.title.incrementImportTitle')}
          {table?.name}
        </p>
      </div>

      {fieldWithPermission && (
        <div className="my-2 h-[400px] overflow-y-auto rounded-sm border border-secondary">
          <InplacePreviewColumn
            onChange={columnHandler}
            workSheets={workSheets}
            fields={fieldWithPermission}
            insertConfig={insertConfig}
          ></InplacePreviewColumn>
        </div>
      )}

      {errorMessage && <p className="pl-2 text-sm text-red-500">{errorMessage}</p>}

      <InplaceImportOptionPanel
        options={options}
        workSheets={workSheets}
        onChange={optionHandler}
      />
    </div>
  );
};

export { InplaceFieldConfigPanel };
